/**
 * Reliable sign-out — avoids axios 401/refresh loops during logout.
 */
import { useAuthStore } from "@store/authStore";

const AUTH_STORAGE_KEY = "lifegram-auth";

function apiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.replace(/\/$/, "");
  }
  if (typeof window !== "undefined" && window.location.hostname !== "localhost") {
    return `${window.location.protocol}//${window.location.host}/api/v1`;
  }
  return "http://localhost:8000/api/v1";
}

let signOutInProgress = false;

export function isSignOutInProgress(): boolean {
  return signOutInProgress;
}

/** Clear session locally and redirect to login (full page reload). */
export async function signOut(): Promise<void> {
  if (signOutInProgress) return;
  signOutInProgress = true;

  const { accessToken, refreshToken } = useAuthStore.getState();

  // Drop client session immediately
  useAuthStore.setState({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
  });

  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    await useAuthStore.persist.clearStorage();
  } catch {
    // ignore storage errors
  }

  // Best-effort server blacklist (raw fetch — no axios interceptors)
  if (refreshToken) {
    try {
      await fetch(`${apiBaseUrl()}/auth/logout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refresh: refreshToken }),
        keepalive: true,
      });
    } catch {
      // Client session already cleared
    }
  }

  window.location.replace("/login");
}
